import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../store/authSlice';
import { setUser } from '../../store/userSlice';
import { setPlayerStats } from '../../store/playerSlice';
import PasswordInput from '../Shared/PasswordInput';
import styles from './CreateUser.module.css';

const CreateUser = () => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [firstname, setFirstName] = useState('');
    const [lastname, setLastName] = useState('');
    const [nickname, setNickName] = useState('');
    const [password1, setPassword1] = useState('');
    const [password2, setPassword2] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const timerRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
    const emailError = emailTouched && email.length > 0 && !isValidEmail(email);

    const passwordsEntered = password1.length > 0 && password2.length > 0;
    const passwordsMatch = password1 === password2;
    const passwordHasNonLetter = /[^a-zA-Z]/.test(password1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (username.length < 5) {
            setError("Username must be at least 5 characters.");
            return;
        }

        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        if (password1.length < 5) {
            setError("Password must be at least 5 characters.");
            return;
        }

        if (!passwordHasNonLetter) {
            setError("Password must contain at least one non-letter character (number or symbol).");
            return;
        }

        if (!passwordsMatch) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        const normalizedUsername = username.toLowerCase();
        const normalizedEmail = email.toLowerCase();
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/c/signup/`, {
                username: normalizedUsername,
                firstname,
                lastname,
                nickname,
                email: normalizedEmail,
                password1,
                password2,
            });
            const { access, refresh, firstname: fn, lastname: ln, nickname: nn, email: em, player } = response.data;
            localStorage.setItem('accessToken', access);
            localStorage.setItem('refreshToken', refresh);
            localStorage.setItem('username', normalizedUsername);
            dispatch(setCredentials({ username: normalizedUsername, accessToken: access, refreshToken: refresh }));
            dispatch(setUser({ username: normalizedUsername, firstname: fn, lastname: ln, nickname: nn, email: em, userID: null }));
            dispatch(setPlayerStats({
                totalGamesPlayed: player.total_games_played,
                totalGamesCompleted: player.total_games_completed,
                wins: player.wins,
                losses: player.losses,
                saves: player.saves,
                savesPerGame: player.saves_per_game,
                halfCups: player.half_cups,
                halfCupsPerGame: player.half_cups_per_game,
                fullCups: player.full_cups,
                fullCupsPerGame: player.full_cups_per_game,
                totalCups: player.total_cups,
                totalCupsPerGame: player.total_cups_per_game,
                ownCups: player.own_cups,
                ownCupsPerGame: player.own_cups_per_game,
                lapsGiven: player.laps_given,
                lapsReceived: player.laps_received,
                totalCupsFor: player.total_cups_for,
                totalCupsAgainst: player.total_cups_against,
                cups: player.cups,
            }));
            setSignupSuccess(true);
            timerRef.current = setTimeout(() => navigate('/main', { state: { fromAuth: true } }), 1050);
            return;
        } catch (error) {
            console.error('Signup error:', error);
            console.error('Response data:', error.response?.data);
            setError(error.response ? error.response.data.error : "Server error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${styles.container} ${signupSuccess ? styles.exitFade : ''}`}>
            <button className={styles.backButton} onClick={() => navigate('/')}>← Back</button>
            <h2 className={styles.title}>Sign Up</h2>
            <form className={styles.form} onSubmit={handleSubmit}>
                <label className={styles.label}>
                    Username
                    <input
                        className={styles.input}
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoFocus
                        autoComplete="username"
                        required
                    />
                </label>
                <label className={styles.label}>
                    Email
                    <input
                        className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => setEmailTouched(true)}
                        autoComplete="email"
                        required
                    />
                    {emailError && (
                        <span className={styles.matchError}>Please enter a valid email address.</span>
                    )}
                </label>
                <label className={styles.label}>
                    First Name
                    <input
                        className={styles.input}
                        type="text"
                        value={firstname}
                        onChange={(e) => setFirstName(e.target.value)}
                        autoComplete="given-name"
                        required
                    />
                </label>
                <label className={styles.label}>
                    Last Name
                    <input
                        className={styles.input}
                        type="text"
                        value={lastname}
                        onChange={(e) => setLastName(e.target.value)}
                        autoComplete="family-name"
                        required
                    />
                </label>
                <label className={styles.label}>
                    Nickname
                    <input
                        className={styles.input}
                        type="text"
                        value={nickname}
                        onChange={(e) => setNickName(e.target.value)}
                        autoComplete="nickname"
                    />
                    <span className={styles.hint}>Optional — defaults to your first name.</span>
                </label>
                <label className={styles.label}>
                    Password
                    <PasswordInput
                        value={password1}
                        onChange={(e) => setPassword1(e.target.value)}
                        autoComplete="new-password"
                        required
                    />
                </label>
                <label className={styles.label}>
                    Confirm Password
                    <PasswordInput
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        autoComplete="new-password"
                        required
                    />
                    {passwordsEntered && (
                        <span className={passwordsMatch ? styles.matchOk : styles.matchError}>
                            {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                        </span>
                    )}
                </label>
                {error && <p className={styles.errorMessage}>{error}</p>}
                <button
                    className={`${styles.submitButton} ${signupSuccess ? styles.submitButtonSuccess : ''}`}
                    type="submit"
                    disabled={loading || signupSuccess}
                >
                    {loading ? 'Creating account…' : signupSuccess ? "You're in!" : 'Sign Up'}
                </button>
            </form>
            <p className={styles.switchLink}>
                Already have an account? <Link to="/login" className={styles.link}>Log in</Link>
            </p>
        </div>
    );
};

export default CreateUser;
